# AN EXPIRED SESSION IS A SIGN-IN SCREEN, NEVER A CRASH SCREEN — card
# 6ab16e9c013e5b1a5ad54628.
#
# PRODUCTION, 2026-09-21 11:46 MT, CrashLog fingerprint bbdc60d. A parent on
# Windows Chrome opened /app#/dashboard with a stored token the server no
# longer honours. Boot tried it twice and the console tail records both:
#
#   1. Login.whoami   -> 401. confirmSessionIdentity is deliberately never
#                        fatal, so it warned and carried on.
#   2. Account.getAccount -> 401, thrown out of the boot read, past
#                        retryAbortedRead (a 401 is not transient, correctly),
#                        into render_react_error and the ErrorBoundary.
#
# So the family's first screen was "We have encountered an error", and the
# only way out of it was the crash screen's Clear & Reload — a button that
# asks a parent to understand localStorage. What should have happened is the
# thing every site does with an expired session: show the sign-in screen.
#
# WHAT THIS MODULE IS. One predicate and one action, so that both places that
# meet the dead token agree about it. `confirmSessionIdentity` meets it first
# and can save the second doomed request; the boot read meets it for a
# principal that has no `whoami` on its manifest at all (a child's Student
# session), and has to handle it too. One definition, two callers — the same
# arrangement `isRetryable` has in transition_recovery.coffee, and for the
# same reason: a token given up on in one place and retried in the other is
# worse than either answer.
#
# WHY 401 AND NOTHING ELSE. These two reads are made with a stored credential
# before the app has drawn anything, and 401 is the server saying the
# credential did not authenticate. A 403 is a principal that IS authenticated
# and may not do this, which is a product question and belongs on the error
# path it already has. A 500 is ours. Neither is a reason to sign somebody out.
#
# WHY NOT forgetIdentity(). crash_reporter's lastKnown identity is what lets
# support name the family behind a login-page crash, and it is cleared on an
# EXPLICIT logout only (crash_reporter.coffee says so in its own words). A
# token that timed out is not the family leaving the device.

# The framework's client API rejects with an axios-shaped error; a bare fetch
# wrapper puts the number somewhere else. Read all three rather than assuming
# which layer produced it.
export statusOf = (err) ->
  err?.response?.status ? err?.status ? err?.statusCode ? null

export isDeadSession = (err) ->
  return false unless err?
  Number(statusOf err) is 401

# Drop the stored session and land on the sign-in screen.
#
# The hash is emptied because the route that was asked for is the route that
# just failed: `#/dashboard` with no session sends ui-router through
# requireLogin to `login` anyway, and arriving there via a redirect from a
# screen that cannot render is a flicker the family does not need to watch.
#
# `showSignIn` is the shell's own render call (start_react). Passed in rather
# than imported, because each shell has its own and this module is shared.
export signOutToSignIn = (showSignIn, deps = {}) ->
  win = deps.window ? (if typeof window != 'undefined' then window else null)
  api = deps.api ? win?.app?.loopbackAPI
  try
    api?.clearUser?()
  catch e
    null
  try
    win.location.hash = '' if win?.location?
  catch e
    null
  showSignIn?()
  true

# A SESSION WHOSE MANIFEST DOES NOT SERVE THE BOOT READ — card
# 6ab69fbe027287c485001e30.
#
# PRODUCTION, 2026-09-25 10:12 MT, CrashLog fingerprint 4d62018a, client
# teacher3.32-c205c71b988a, route /teacher#/tokens. Three milliseconds after
# "Calling ready":
#
#   TypeError: this.loopbackAPI.Account.getAccount is not a function
#
# The shell found a token under `$teacher$`, so `isLoggedIn()` answered true
# and boot went straight into `Application.refresh()`. But `/clientapi` had
# answered 200 with the ANONYMOUS manifest — it does that whenever a presented
# token authorizes to no usable role, not only when no token is presented — and
# on that manifest `Account` carries `getPlans` and nothing else. The client
# believed it had a session; the manifest it was holding disagreed, and nothing
# reconciled the two. The family got the crash screen.
#
# THIS IS NOT THE 401 CASE ABOVE, and that is why it needs its own predicate.
# A dead token is a request that comes back refused; this one never gets to
# make a request at all. Calling an absent method throws SYNCHRONOUSLY, before
# any promise exists to reject, so every `.catch` downstream is already too
# late — the same reading as the games screen's audio warm-up (card
# 6aaedd51a822f303b055f82e): ask the manifest what it will serve.
#
# WHY `Account.getAccount` BY NAME rather than something more general. It is
# what `Application.refresh` calls, and `refresh` is the boot read of every
# shell. A shell whose boot read changes should change this with it, loudly,
# rather than inherit a guard that no longer guards the call being made.
export servesBootRead = (api) ->
  typeof api?.Account?.getAccount == 'function'
